import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, catchError, switchMap } from 'rxjs';

import { CurrentAthleteService } from '../../../shared/current-athlete.service';
import { PmNoticeDevice, pmPct } from '../pm-validation.models';
import { PmValidationService } from '../pm-validation.service';

/**
 * Globale Hinweisleiste „Leistungsmesser weicht ab" — neben den anderen
 * Bannern in `app.component`, im selben Muster wie die Doppelaufzeichnungen.
 *
 * Für ALLE Nutzer sichtbar, inklusive der Abweichung in Prozent. Nur der Weg
 * in die Werkbank (Radgewicht, Crr, CdA, Ausschlüsse) hängt an
 * `tools.pm_validation` — das Backend sagt mit `can_open_tool`, ob er erlaubt
 * ist; geprüft wird dort ohnehin erneut.
 *
 * Lädt bei Athletenwechsel neu und bleibt bei jedem Fehler still: die Leiste
 * steht über jeder Seite der App und darf keine davon stören.
 */
@Component({
  selector: 'app-pm-notice-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, MatButtonModule, MatIconModule, TranslateModule],
  templateUrl: './pm-notice-banner.component.html',
  styleUrls: ['./pm-notice-banner.component.scss'],
})
export class PmNoticeBannerComponent implements OnInit {
  private readonly svc = inject(PmValidationService);
  private readonly currentAthlete = inject(CurrentAthleteService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  devices: PmNoticeDevice[] = [];
  canOpenTool = false;
  athleteId: number | null = null;
  private suppressed = false;

  readonly pct = pmPct;

  ngOnInit(): void {
    const sub = this.currentAthlete.athleteId$.pipe(
      switchMap((id) => {
        // Athletenwechsel: der Hinweis gilt dem neuen Sportler, also auch
        // ein vorher weggeklickter wieder anzeigen.
        this.suppressed = false;
        this.athleteId = id;
        this.devices = [];
        this.cdr.markForCheck();
        return this.svc.notice(id).pipe(catchError(() => EMPTY));
      }),
    ).subscribe((res) => {
      this.devices = this.suppressed ? [] : (res.devices ?? []);
      this.canOpenTool = Boolean(res.can_open_tool);
      this.cdr.markForCheck();
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  high(d: PmNoticeDevice): boolean {
    return d.verdict === 'high';
  }

  dismiss(): void {
    this.suppressed = true;
    this.devices = [];
    this.cdr.markForCheck();
  }
}
